import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "../styles/sidebar.css";
import googleDriveIcon from "../images/nav-icons/google-drive.svg";
import googleCalendarIcon from "../images/nav-icons/Calendar_Google_Default.png";
import ResourcesActiveIcon from "../images/nav-icons/Resources-active.svg";
import ProgramNavIcon from "../images/program/program-icon.png";
import ProfileActiveIcon from "../images/buttons/Athlete-profile-active.svg";
import TeamActiveIcon from "../images/nav-icons/Team-active.svg";
import AthleteDirectoryActiveIcon from "../images/buttons/student-directory-active.svg";
import BillingIcon from "../images/nav-icons/Billing.svg";
import BillingActiveIcon from "../images/nav-icons/Billing-active.svg";
import AiAssistantActiveIcon from "../images/nav-icons/Polaris-Assist-black-star.svg";
import AiAssistantInactiveIcon from "../images/nav-icons/Polaris-Assist-inactive.svg";
import { useAssistAccess } from "../hooks/useAssistAccess";
import { useAuth } from "../contexts/AuthContext";
import { useIncompleteAdminProfileOptional } from "../contexts/IncompleteAdminProfileContext";
import RailSidebarShell from "./RailSidebarShell";

const ICONS = {
    profile: ProfileActiveIcon,
    program: ProgramNavIcon,
    programActive: ProgramNavIcon,
    team: TeamActiveIcon,
    athletes: AthleteDirectoryActiveIcon,
    resources: ResourcesActiveIcon,
    billing: BillingIcon,
    billingActive: BillingActiveIcon,
    calendar: googleCalendarIcon,
    drive: googleDriveIcon,
    aiAssistantActive: AiAssistantActiveIcon,
    aiAssistantInactive: AiAssistantInactiveIcon,
};

type NavItem = {
    id: string;
    label: string;
    iconSrc: string;
    path?: string;
    href?: string;
    group?: string;
    adminOnly?: boolean;
};

const items: NavItem[] = [
    { id: "profile",   label: "Profile",           iconSrc: ICONS.profile,   path: "/admin-profile" },
    { id: "program",   label: "Hub",               iconSrc: ICONS.program, path: "/program" },
    { id: "team",      label: "Team",              iconSrc: ICONS.team,      path: "/team" },
    { id: "athletes",  label: "Directory",         iconSrc: ICONS.athletes,  path: "/athletes-directory" },
    { id: "resources", label: "Courses",           iconSrc: ICONS.resources, path: "/resources" },
    { id: "billing",   label: "Billing",           iconSrc: ICONS.billing,   path: "/billing",     adminOnly: true },
    { id: "ai-assistant", label: "Polaris Assist", iconSrc: ICONS.aiAssistantInactive, path: "/ai-assistant" },
    { id: "calendar",  label: "Calendar",          iconSrc: ICONS.calendar,  path: "/calendar" },
    { id: "drive",     label: "Drive",             iconSrc: ICONS.drive,     path: "/drive" },
];

function resolveIconSrc(it: NavItem, isActive: boolean): string {
    if (it.id === "billing" && isActive) return ICONS.billingActive;
    if (it.id === "ai-assistant" && isActive) return ICONS.aiAssistantActive;
    if (it.id === "ai-assistant") return ICONS.aiAssistantInactive;
    if (it.id === "program" && isActive) return ICONS.programActive;
    return it.iconSrc;
}

function SideLinkContent({
    it,
    isActive,
}: {
    it: NavItem;
    isActive: boolean;
    index?: number;
}) {
    // Program uses a full-color PNG when active; Assist swaps to the black-star brand SVG
    // (inactive Assist is forced white via rail CSS so the gray SVG fill does not show).
    const imgClass =
        (it.id === "program" && isActive) || (it.id === "ai-assistant" && isActive)
            ? "side-img side-img--fullcolor"
            : "side-img";

    return (
        <>
            <span className="side-ico">
                <img
                    className={imgClass}
                    src={resolveIconSrc(it, isActive)}
                    alt=""
                    aria-hidden="true"
                />
            </span>
            <span className="side-label">{it.label}</span>
        </>
    );
}

export type SidebarProps = {
    companyLogoUrl?: string | null;
    /** Remount key so a replaced logo reloads even if the URL string is unchanged. */
    companyLogoVersion?: number;
    profileImageUrl?: string | null;
    onCompanyLogoError?: () => void;
};

export default function Sidebar({
    companyLogoUrl = null,
    companyLogoVersion = 0,
    profileImageUrl = null,
    onCompanyLogoError,
}: SidebarProps) {
    const location = useLocation();
    const { userInfo, isLoading, hasProfile } = useAuth();
    const incompleteProfileCtx = useIncompleteAdminProfileOptional();
    const { visible: showAssist } = useAssistAccess();

    const role = userInfo?.role || localStorage.getItem("userRole");

    const isAthleteUser = role
        ? role.toLowerCase().includes("athlete") || role.toLowerCase().includes("student")
        : false;

    const isAdministrator = role
        ? role.toLowerCase().includes("administrator")
        : false;

    const isAdminStaff = !isAthleteUser;

    const visibleItems = useMemo(() => {
        return items.filter(
            (item) =>
                (!item.adminOnly || isAdministrator)
                && (item.id !== "ai-assistant" || showAssist),
        );
    }, [isAdministrator, showAssist]);

    if (isLoading) {
        return null;
    }

    const setupIncomplete = incompleteProfileCtx?.setupIncomplete === true;
    const blockSidebarNavigation = isAdminStaff && (!hasProfile || setupIncomplete);

    const handleBlockedNavClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        incompleteProfileCtx?.triggerValidation();
    };

    const nav = (
        <nav className="side-nav">
            {visibleItems.map((it, index) => {
                const isActive = Boolean(it.path && location.pathname === it.path);
                const linkClass = `side-link ${isActive ? "is-active" : ""} ${index === 0 ? "profile-margin" : ""} ${blockSidebarNavigation ? "disabled" : ""}`;
                const disabledStyle = blockSidebarNavigation
                    ? { opacity: 0.5, cursor: "not-allowed" as const }
                    : undefined;

                return (
                    <div key={it.id}>
                        {it.group && <div className="side-group">{it.group}</div>}

                        {it.path ? (
                            blockSidebarNavigation ? (
                                <button
                                    type="button"
                                    className={linkClass}
                                    data-testid={`sidebar-${it.id}`}
                                    onClick={handleBlockedNavClick}
                                    style={disabledStyle}
                                    aria-disabled="true"
                                >
                                    <SideLinkContent it={it} isActive={isActive} index={index} />
                                </button>
                            ) : (
                                <NavLink
                                    to={it.path}
                                    className={({ isActive: navActive }) =>
                                        `side-link ${navActive ? "is-active" : ""} ${index === 0 ? "profile-margin" : ""}`
                                    }
                                    end
                                    data-testid={`sidebar-${it.id}`}
                                >
                                    {({ isActive: navActive }) => (
                                        <SideLinkContent it={it} isActive={navActive} index={index} />
                                    )}
                                </NavLink>
                            )
                        ) : (
                            <a
                                className={linkClass}
                                href={it.href}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`sidebar-${it.id}`}
                                onClick={blockSidebarNavigation ? handleBlockedNavClick : undefined}
                                style={disabledStyle}
                                aria-disabled={blockSidebarNavigation ? true : undefined}
                            >
                                <SideLinkContent it={it} isActive={false} index={index} />
                            </a>
                        )}
                    </div>
                );
            })}
        </nav>
    );

    if (!isAdminStaff) {
        return <aside className="sidebar">{nav}</aside>;
    }

    return (
        <RailSidebarShell
            headerLogoUrl={companyLogoUrl}
            headerLogoKey={companyLogoVersion}
            footerAvatarUrl={profileImageUrl}
            footerAvatarAlt="Your profile"
            confirmLogout
            showSubmitIdea
            onHeaderLogoError={onCompanyLogoError}
            className="admin-staff-sidebar"
            data-testid="admin-staff-rail-sidebar"
        >
            {nav}
        </RailSidebarShell>
    );
}
